import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import WITHDRAWAL_ICON from "@redotech/redo-icons/x-circle.svg";
import WITHDRAWAL_ICON_FILLED from "@redotech/redo-icons/x-circle_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import { recordListHref } from "../../../../return/record-links/record-links";
import {
  useRefreshWithdrawalCountOnNavigate,
  useWithdrawalNeedsActionCount,
} from "../../../../return/withdrawals/use-withdrawal-needs-action-count";

const WithdrawalItemContent = memo(function WithdrawalItemContent() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const withdrawalMatch = useMatch(`${routes.withdrawalHref}/*`);
  const anyChildSelected = !!withdrawalMatch;
  const needsActionCount = useWithdrawalNeedsActionCount();
  useRefreshWithdrawalCountOnNavigate();

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getWithdrawalHref() }}
      anyChildSelected={anyChildSelected}
      data-dd-action-name="returns_nav:withdrawals_link_click"
      FilledIcon={WITHDRAWAL_ICON_FILLED}
      friendlyName={t("Withdrawals", {
        context:
          "returns navigation label for customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
      })}
      id="withdrawal"
      notificationCount={needsActionCount}
      UnfilledIcon={WITHDRAWAL_ICON}
    >
      <AllWithdrawalsItem />
      <WithdrawalNeedsActionItem notificationCount={needsActionCount} />
    </RedoSecondaryNavItem>
  );
});

export const WithdrawalItem = memo(function WithdrawalItem() {
  const hasPermissionToViewItem = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,
  );
  const withdrawalEnabled = useCapability("withdrawals");

  if (!hasPermissionToViewItem || !withdrawalEnabled) {
    return null;
  }

  return <WithdrawalItemContent />;
});

const AllWithdrawalsItem = memo(function AllWithdrawalsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: recordListHref(routes.returnsBaseUrl, ReturnTypeEnum.WITHDRAWAL),
      }}
      data-dd-action-name="returns_nav:all_withdrawals_link_click"
      friendlyName={t("All withdrawals", {
        context:
          "returns navigation label for all customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
      })}
      id="all-withdrawals"
    />
  );
});

const WithdrawalNeedsActionItem = memo(function WithdrawalNeedsActionItem({
  notificationCount,
}: {
  notificationCount?: number;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getWithdrawalSubRoute("needs-action"),
      }}
      data-dd-action-name="returns_nav:withdrawals_needs_action_link_click"
      friendlyName={t("Needs action", {
        context:
          "withdrawal status navigation label for requests requiring merchant action",
      })}
      id="withdrawal-needs-action"
      notificationCount={notificationCount}
    />
  );
});
